import type { ListBuilderConfiguration } from '../types';

/**
 * Purchase Order Vendor Embedded list configuration
 * Shows purchase orders embedded in a vendor view
 * Features conditional color rules for 10 different status values
 * Converted from JSON to TypeScript for type safety
 */
export const PURCHASE_ORDER_VENDOR_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-purchase-orders-vendor-embedded',
			cacheTTL: 600000,
			name: 'Vendor Purchase Orders',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'po-number',
					fields: [
						{
							id: 'po-number-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'purchase_order',
								idField: '_id',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'externalId',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'primary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'PO Number' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'externalId',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'statusLabel',
								},
								defaultValue: 'Unknown',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'conditional',
									rules: [
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '0',
											color: { type: 'neutral' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '1',
											color: { type: 'info' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '2',
											color: { type: 'success' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '3',
											color: { type: 'info' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '4',
											color: { type: 'warning' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '5',
											color: { type: 'success' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '6',
											color: { type: 'warning' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '7',
											color: { type: 'neutral' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '8',
											color: { type: 'warning' },
										},
										{
											field: { fieldName: 'status' },
											operator: 'equals',
											value: '9',
											color: { type: 'danger' },
										},
									],
									defaultColor: { type: 'neutral' },
								},
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h2', text: 'Status' }],
					minWidth: 140,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'status',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'requested-date',
					fields: [
						{
							id: 'requested-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'requestedDate',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h3', text: 'Requested Date' }],
					minWidth: 120,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'requestedDate',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'order-date',
					fields: [
						{
							id: 'order-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'date',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h4', text: 'Order Date' }],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'date',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'item-count',
					fields: [
						{
							id: 'item-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemCount',
								},
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h5', text: 'Items' }],
					minWidth: 80,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'itemCount',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'total',
					fields: [
						{
							id: 'total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'total',
								},
								defaultValue: '0.00',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'h6', text: 'Total' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'total',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
			],
			description: 'Purchase orders for this vendor',
			defaultSortId: 'column-order-date-desc',
			requiredFields: [
				'purchaseOrder.id',
				'purchaseOrder.externalId',
				'purchaseOrder.status',
				'purchaseOrder.date',
				'purchaseOrder.requestedDate',
				'purchaseOrder.vendorId',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['purchaseOrderKeywords', 'purchaseOrderStatus'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/purchase-orders/:id',
			objectLabel: 'Purchase Order',
			objectTable: 'purchase_order',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'date', fieldLabel: 'Order Date' },
				{ fieldName: 'requestedDate', fieldLabel: 'Requested Date' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'po-number',
			name: 'PO Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'externalId',
				},
			],
			description: 'Sort by PO number',
		},
		{
			id: 'status',
			name: 'Status',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'status',
				},
			],
			description: 'Sort by status',
		},
		{
			id: 'order-date',
			name: 'Order Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'date',
				},
			],
			description: 'Sort by order date (newest first)',
		},
		{
			id: 'requested-date',
			name: 'Requested Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'requestedDate',
				},
			],
			description: 'Sort by requested date (newest first)',
		},
	],
};
